import { useContext, useEffect, useState, useCallback } from 'react';
import classNames from 'classnames/bind';
import { gql } from '@apollo/client';
import { Container, HeaderNavigationMenu, SkipNavigationLink, OuroLogo, OuroLogoWithTagline } from '@/components';
import styles from './Header.module.scss';
import Link from 'next/link';
import { NetspendContext } from 'library/contexts/NetspendContext';

let cx = classNames.bind(styles);

export function Header({ title = 'Netspend', menuItems, navigationSettings }) {
	const [isNavShown, setIsNavShown] = useState(false);
	const { stripeHeight } = useContext(NetspendContext);
	const ctaHasBeenOverridden = false;

	const closeOnEsc = useCallback((e) => {
		if (e.key == 'Escape' || e.key == 'Esc') {
			document.getElementById('nav-close-button').focus();
			document.getElementById('nav-close-button').click();
			document.body.removeEventListener('keydown', closeOnEsc);
		}
	}, []);
	const handleNavClose = useCallback(() => {
		setIsNavShown(false);
		document.body.classList.remove('display-mobile-menu', 'hide-body-overflow');
		document.getElementById('nav-open-button').focus();
	}, []);
	const handleNavOpen = useCallback(() => {
		setIsNavShown(true);
		document.body.classList.add('display-mobile-menu', 'hide-body-overflow');
		document.getElementById('nav-close-button').focus();
		document.body.addEventListener('keydown', closeOnEsc);
	}, []);

	return (
		<header className={cx(['ns_header', 'header', navigationSettings?.darkBackground && 'dark-background'])} style={stripeHeight && { '--stripeHeight': `${stripeHeight}px` }}>
			<SkipNavigationLink />
			<Container className={cx('header-container')}>
				<div className={cx('navbar')}>
					<div className={cx('logo-container')}>
						<Link href="/" onClick={handleNavClose}>
							<OuroLogo className={cx(['logo', 'logo-mobile'])} />
              <OuroLogoWithTagline className={cx(['logo', 'logo-desk'])} />
						</Link>
					</div>
					<div className={cx('mobile-container')}>
						<div className={cx('nav-toggle')}>
							<button id="nav-open-button" type="button" className={cx('nav-open-button')} onClick={handleNavOpen} aria-label="Open the navigation menu" aria-controls="primary-navigation" aria-expanded={isNavShown} aria-hidden={isNavShown} focusable={!isNavShown} tabIndex={isNavShown ? -1 : 0}>
								<svg width="24" height="15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
									<path d="M.95 2.85h22.1c.47 0 .85-.269.85-.6V.75c0-.331-.38-.6-.85-.6H.95C.48.15.1.419.1.75v1.5c0 .331.38.6.85.6Zm0 6h22.1c.47 0 .85-.269.85-.6v-1.5c0-.331-.38-.6-.85-.6H.95c-.47 0-.85.269-.85.6v1.5c0 .331.38.6.85.6Zm0 6h22.1c.47 0 .85-.269.85-.6v-1.5c0-.331-.38-.6-.85-.6H.95c-.47 0-.85.269-.85.6v1.5c0 .331.38.6.85.6Z" fill="#ffffff" />
								</svg>
							</button>
							<button type="button" id="nav-close-button" className={cx('nav-close-button')} onClick={handleNavClose} aria-label="Close the navigation menu" aria-controls="primary-navigation" aria-expanded={isNavShown} aria-hidden={!isNavShown} focusable={isNavShown} tabIndex={isNavShown ? 0 : -1}>
								<svg width="17" height="17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
									<path d="M15.944 14.532a1.144 1.144 0 0 1-1.619 1.619l-6.044-6.046-6.046 6.044A1.144 1.144 0 0 1 .617 14.53l6.046-6.044L.619 2.44A1.144 1.144 0 0 1 2.237.822l6.044 6.046L14.327.821a1.144 1.144 0 1 1 1.619 1.618L9.9 8.486l6.044 6.046Z" fill="#000000" />
								</svg>
							</button>
						</div>
					</div>
					<HeaderNavigationMenu className={cx(['primary-navigation'])} menuItems={menuItems} handleNavClose={handleNavClose} ctaOverride={navigationSettings?.overrideHeaderCta} ctaHasBeenOverridden={ctaHasBeenOverridden} />
				</div>
			</Container>
		</header>
	);
}

Header.fragments = {
	entry: gql`
		fragment PageNavigationSettings on NavigationSettings {
			darkBackground
			overrideHeaderCta {
				target
				title
				url
			}
		}
	`,
};
